/**
 * Streamlined Video Info Display Component
 * Responsive design: Desktop (thumbnail left + options right) | Mobile (thumbnail top + options bottom)
 */

import React from 'react';
import { useTranslation } from 'next-i18next';
import { formatBytes } from '../../lib/utils/format';
import { Download, Video, Clock } from 'lucide-react';
import type { VideoFormat } from '../../lib/hooks/useVideoInfo';

interface VideoInfoDisplayProps {
  title: string;
  thumbnail?: string;
  duration: number;
  formats: VideoFormat[];
  selectedFormat: { format: string; quality: string } | null;
  onSelectFormat: (format: string, quality: string) => void;
  onDownload?: (format: VideoFormat) => void;
  disabled?: boolean;
  downloadLoading?: boolean;
  className?: string;
}

const VideoInfoDisplay: React.FC<VideoInfoDisplayProps> = ({
  title,
  thumbnail,
  duration,
  formats,
  onDownload,
  disabled = false,
  downloadLoading = false,
  className = '',
}) => {
  const { t } = useTranslation('common');

  // Format duration to MM:SS
  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Clean size display
  const getCleanSizeDisplay = (size: number, resolution?: string, duration?: number) => {
    const formatted = formatBytes(size, resolution, duration);
    return formatted.replace(/\s*\(ước tính\)/g, '').replace(/^~/, '');
  };

  return (
    <div className={`w-full max-w-5xl mx-auto ${className}`}>
      {/* Clean Card */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        
        {/* Video Title - Full width header */}
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white line-clamp-2 mb-2">
            {title}
          </h3>
          <div className="flex items-center gap-3 md:gap-4 text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              <span className="font-medium">{formatDuration(duration)}</span>
            </div>
            <span>•</span>
            <span className="font-medium">{formats.length} quality options</span>
          </div>
        </div>

        {/* Main Content: Responsive Layout */}
        <div className="p-4 md:p-6">
          {/* Desktop Layout: Thumbnail + Options side by side */}
          <div className="hidden md:flex gap-8">
            
            {/* Thumbnail - Desktop only */}
            <div className="flex-shrink-0">
              {thumbnail ? (
                <div className="relative rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={thumbnail}
                    alt={title}
                    className="w-40 h-24 object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded-md backdrop-blur-sm">
                    {formatDuration(duration)}
                  </div>
                </div>
              ) : (
                <div className="w-40 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-xl flex items-center justify-center shadow-lg">
                  <Video className="w-8 h-8 text-gray-400" />
                </div>
              )}
            </div>

            {/* Quality Options - Desktop */}
            <div className="flex-1">
              <div className="space-y-4">
                {formats.map((format, index) => {
                  const sizeDisplay = getCleanSizeDisplay(format.size, format.resolution, duration);
                  
                  return (
                    <div
                      key={index}
                      className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                    >
                      {/* Quality Badge */}
                      <div className="flex items-center gap-2 lg:gap-4 flex-wrap">
                        <span className={`
                          px-4 py-2 rounded-lg text-sm font-bold text-center flex-shrink-0
                          ${format.quality.includes('720') ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                            format.quality.includes('480') ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                            'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                          }
                        `}>
                          {format.quality}
                        </span>
                        {/* Type label */}
                        <span className={`
                          px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0
                          ${
                            format.type === 'progressive'
                              ? 'bg-blue-200 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                              : format.type === 'video'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                                : format.type === 'audio'
                                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-300'
                          }
                        `}>
                          {format.type === 'progressive'
                            ? 'Video+Audio'
                            : format.type === 'video'
                              ? 'Video Only'
                              : format.type === 'audio'
                                ? 'Audio Only'
                                : 'Unknown'}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium flex-shrink-0">
                          {format.resolution}
                        </span>
                        {/* File Size - Inline on mobile, separate on desktop */}
                        <span className="lg:hidden text-sm font-semibold text-gray-700 dark:text-gray-300">
                          • {sizeDisplay}
                        </span>
                      </div>

                      {/* File Size - Desktop only */}
                      <div className="hidden lg:block lg:flex-1 text-center">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {sizeDisplay}
                        </span>
                      </div>

                      {/* Download Button */}
                      <button
                        onClick={() => onDownload?.(format)}
                        disabled={disabled || downloadLoading}
                        className={`
                          w-full lg:w-auto px-6 lg:px-8 py-3 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 flex-shrink-0
                          ${disabled || downloadLoading
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                          }
                        `}
                      >
                        {downloadLoading ? (
                          <>
                            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Downloading</span>
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            <span>Download</span>
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mobile Layout: Thumbnail on top + Options below */}
          <div className="md:hidden">
            {/* Thumbnail - Mobile: centered on top */}
            <div className="flex justify-center mb-6">
              {thumbnail ? (
                <div className="relative rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={thumbnail}
                    alt={title}
                    className="w-32 h-20 object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                    {formatDuration(duration)}
                  </div>
                </div>
              ) : (
                <div className="w-32 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-xl flex items-center justify-center shadow-lg">
                  <Video className="w-6 h-6 text-gray-400" />
                </div>
              )}
            </div>

            {/* Quality Options - Mobile: below thumbnail */}
            <div className="space-y-3">
              {formats.map((format, index) => {
                const sizeDisplay = getCleanSizeDisplay(format.size, format.resolution, duration);
                
                return (
                  <div
                    key={index}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    {/* Top/Left: Quality + Resolution + Size */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`
                        px-3 py-1.5 rounded-lg text-sm font-bold flex-shrink-0
                        ${format.quality.includes('720') ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                          format.quality.includes('480') ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                          'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                        }
                      `}>
                        {format.quality}
                      </span>
                      {/* Type label */}
                      <span className={`
                        px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0
                        ${
                          format.type === 'progressive'
                            ? 'bg-blue-200 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                            : format.type === 'video'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                              : format.type === 'audio'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-300'
                        }
                      `}>
                        {format.type === 'progressive'
                          ? 'Video+Audio'
                          : format.type === 'video'
                            ? 'Video Only'
                            : format.type === 'audio'
                              ? 'Audio Only'
                              : 'Unknown'}
                      </span>
                      <div className="text-sm text-gray-600 dark:text-gray-400 flex-shrink-0">
                        <span className="font-medium">{format.resolution}</span>
                        <span className="mx-1">•</span>
                        <span className="font-semibold text-gray-700 dark:text-gray-300">{sizeDisplay}</span>
                      </div>
                    </div>

                    {/* Bottom/Right: Download Button */}
                    <button
                      onClick={() => onDownload?.(format)}
                      disabled={disabled || downloadLoading}
                      className={`
                        w-full sm:w-auto px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 flex-shrink-0
                        ${disabled || downloadLoading
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'
                        }
                      `}
                    >
                      {downloadLoading ? (
                        <>
                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Downloading</span>
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Helper Text */}
          <div className="mt-4 md:mt-6 text-center">
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              Click any download button to start downloading in that quality
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoInfoDisplay;
